import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface Venue {
  id: string;
  name: string;
  isActive: boolean;
}

export const VenuesPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Venue | null>(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);

  const list = useQuery({
    queryKey: ['venues'],
    queryFn: () => api.get<{ data: Venue[] }>('/venues?pageSize=100'),
  });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setIsActive(editing.isActive);
    } else {
      setName('');
      setIsActive(true);
    }
  }, [open, editing]);

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch<Venue>(`/venues/${editing.id}`, { name, isActive })
        : api.post<Venue>('/venues', { name }),
    onSuccess: () => {
      toast.success(t('venues.saved'));
      setOpen(false);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Venue>[] = [
    {
      id: 'name',
      header: t('venues.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'status',
      header: t('venues.columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? t('venues.statusActive') : t('venues.statusInactive')}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('venues.title')}
        description={t('venues.description')}
        tipId="settings.venues.purpose"
        headerActions={
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            {t('venues.create')}
          </Button>
        }
      />

      <DataTable
        rows={list.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('venues.empty')}</p>}
        onRowClick={(row) => {
          setEditing(row);
          setOpen(true);
        }}
        rowActions={(row) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing(row);
              setOpen(true);
            }}
          >
            {tCommon('action.edit')}
          </Button>
        )}
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setEditing(null);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{editing ? t('venues.edit') : t('venues.create')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!name.trim()) return;
              save.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('venues.name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            {editing ? (
              <label className="flex items-center justify-between gap-3 text-body-sm">
                <span>{t('venues.active')}</span>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </label>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
                {t('venues.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
